import React, { useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  TrendingDown,
  TrendingUp,
  AttachMoney,
  CalendarToday,
  Search,
  Undo,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
// import { useLedger } from "../../hooks/useLedger";
import { formatCurrencyN } from "../../../../common/utils_t";
 import { useLedger } from "../../../../hooks/useLedger";
import ConfirmationModal from "../../../../components/ConfirmationModal";
// import ConfirmationModal from "../../components/ConfirmationModal";

const PostingsByPostCode: React.FC = () => {
  const {
    transactions,
    summary,
    loading,
    getPostingsByPostCode,
    initiateReversal,
  } = useLedger();
  const [postCode, setPostCode] = useState("");
  const [reportGenerated, setReportGenerated] = useState(false);
  const [showReversalModal, setShowReversalModal] = useState(false);

  const handleGenerateReport = async () => {
    const success = await getPostingsByPostCode(postCode.trim());

    if (success) {
      setReportGenerated(true);
    }
  };

  const calculateRunningBalance = (index: number): number => {
    let balance = 0;
    for (let i = 0; i <= index; i++) {
      const credit = parseFloat(String(transactions[i].cr)) || 0;
      const debit = parseFloat(String(transactions[i].dr)) || 0;
      balance += credit - debit;
    }
    return balance;
  };

  const handleInitiateReversal = () => {
    setShowReversalModal(true);
  };

  const handleConfirmReversal = async () => {
    setShowReversalModal(false);
    if (postCode.trim()) {
      const success = await initiateReversal(postCode.trim());
      if (success) {
        // Optionally refresh the report after reversal
        await getPostingsByPostCode(postCode.trim());
      }
    }
  };

  const handleCancelReversal = () => {
    setShowReversalModal(false);
  };

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Transaction Search</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                Transaction Search
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fetch postings by post code
              </Typography>
            </Box>

            {/* Report Filters */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Enter Post Code
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Post Code"
                    value={postCode}
                    required
                    onChange={(e) => setPostCode(e.target.value)}
                    sx={{ fontSize: "0.75rem", width: "100%" }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Search />}
                    onClick={handleGenerateReport}
                    disabled={loading}
                    sx={{ height: "56px", fontSize: "0.75rem" }}
                  >
                    Search Transaction
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Summary Cards - Only show after report is generated */}
            {reportGenerated && summary && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#ffebee" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <TrendingDown sx={{ color: "#d32f2f", mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#d32f2f" }}
                        >
                          Total Debit (Dr)
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#d32f2f" }}
                      >
                        {formatCurrencyN(summary.totalDebit)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#e8f5e9" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <TrendingUp sx={{ color: "#2e7d32", mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#2e7d32" }}
                        >
                          Total Credit (Cr)
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#2e7d32" }}
                      >
                        {formatCurrencyN(summary.totalCredit)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#e3f2fd" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <AttachMoney sx={{ color: "#1976d2", mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#1976d2" }}
                        >
                          Net Balance (Cr - Dr)
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#1976d2" }}
                      >
                        {formatCurrencyN(summary.netBalance)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#e0f2f1" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <CalendarToday sx={{ color: "#00695c", mr: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#00695c" }}
                        >
                          Transactions
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#00695c" }}
                      >
                        {summary.transactionCount}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Transaction History Table - Only show after report is generated */}
            {reportGenerated && transactions.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                      Transaction History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Showing {transactions.length} transactions
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleInitiateReversal}
                  >
                    <Undo />
                    Initiate Reversal
                  </Button>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Debit (Dr)</TableCell>
                        <TableCell align="right">Credit (Cr)</TableCell>
                        <TableCell align="right">Balance (Cr - Dr)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction, index) => {
                        const debit = parseFloat(String(transaction.dr)) || 0;
                        const credit = parseFloat(String(transaction.cr)) || 0;
                        const balance = calculateRunningBalance(index);
                        return (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              {dayjs(transaction.dateCreated).format(
                                "DD MMM YYYY HH:mm"
                              )}
                            </TableCell>
                            <TableCell>{transaction.desp || "-"}</TableCell>
                            <TableCell align="right">
                              {debit > 0 ? formatCurrencyN(debit) : "-"}
                            </TableCell>
                            <TableCell align="right">
                              {credit > 0 ? formatCurrencyN(credit) : "-"}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrencyN(balance)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Empty State */}
            {reportGenerated && transactions.length === 0 && (
              <Paper sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary">
                  No transactions found for the selected period
                </Typography>
              </Paper>
            )}
          </Box>
        </LocalizationProvider>
      </IonContent>
      <IonLoading isOpen={loading} message="Generating report..." />
      <ConfirmationModal
        isOpen={showReversalModal}
        header="Confirm Reversal"
        message="Are you sure you want to initiate reversal?"
        onConfirm={handleConfirmReversal}
        onCancel={handleCancelReversal}
      />
    </IonPage>
  );
};

export default PostingsByPostCode;
